import React, { useState } from "react";

import {
  AcceptanceButton,
  AcceptanceFooter,
} from "layouts/AcceptancePage/components";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Brand } from "@ds-developer/models";
import { SignedUrl } from "@hubcapp/graphql/types/gateway";
import { useHistory } from "react-router-dom";
import { useMutationCreateSigningdocuments } from "../hooks";
import DocumentList from "./DocumentList/index";
import clsx from "clsx";

type Props = {
  documents: SignedUrl[] | undefined;
  agreementKey: string;
  brand: string;
};

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: 700,
    "&.bsm": {
      fontWeight: 500,
    },
  },
  boldLink: {
    fontWeight: 700,
    "&.bsm": {
      fontWeight: 300,
    },
    textDecoration: "underline !important",
    color: "#07828D",
  },
  privacyText: {
    fontWeight: 400,
    "&.bsm": {
      fontWeight: 300,
    },
    fontSize: "16px",
    lineHeight: "20px",
    color: "#2B2D32",
    marginTop: "3%",
  },
  subText: {
    fontWeight: 400,
    "&.bsm": {
      fontWeight: 300,
    },
    fontSize: "18px",
    lineHeight: "27px",
    color: "#2B2D32",
    marginTop: "3%",
  },
  sectionStyle: {
    marginBottom: "48px",
  },
  formControlLabel: {
    fontWeight: 700,
    "&.bsm *": {
      fontWeight: 300,
    },
    fontSize: "16px",
    letterSpacing: "0.3375px",
    color: "#2B2929",
  },
  smallText: {
    "&.bsm": {
      fontWeight: 300,
    },
  },
}));

const FranAcceptanceDocumentsPage: React.FC<Props> = ({
  documents,
  agreementKey,
  brand,
}) => {
  const classes = useStyles();
  const [isTermsConditionschecked, setsTermsConditionschecked] = useState(
    false,
  );
  const [isDocumentsRead, setIsDocumentsRead] = useState(false);

  const history = useHistory();

  const urlPrivacyPolicy =
    brand === Brand.Bsm
      ? "https://www.bsm.co.uk/franchisee-privacy-notice"
      : "https://www.theaa.com/privacy-notice";

  const [
    signDocuments,
    { loading: isSigningDocuments },
  ] = useMutationCreateSigningdocuments(agreementKey, {
    onCompleted: () => {
      history.push(`/acceptance-page-result/success/${brand}/${agreementKey}`);
    },
    onError: () => {
      history.push(`/acceptance-page-result/error/${brand}`);
    },
  });

  return (
    <>
      <Typography
        variant="h1"
        className={clsx(classes.title, {
          bsm: brand === "BSM",
        })}
      >
        Your Documents
      </Typography>
      <div className={classes.sectionStyle}>
        <p
          className={clsx(classes.subText, {
            bsm: brand === "BSM",
          })}
        >
          Please open and read the documents below in numerical order. They
          contain pre-contract information and the terms of your agreement with
          us, so it&apos;s important to read and understand them.
        </p>
        <p
          className={clsx(classes.subText, {
            bsm: brand === "BSM",
          })}
        >
          Before accepting the terms, please make sure to download or print a
          copy of the Franchise Agreement and Driver Handbook for your records.
        </p>
        <DocumentList
          brand={brand}
          documents={documents}
          onAllDocumentsRead={() => setIsDocumentsRead(true)}
        />
      </div>
      <div>
        <div className={classes.sectionStyle}>
          <p
            className={clsx(classes.subText, {
              bsm: brand === "BSM",
            })}
          >
            You are entering into a hire agreement regulated by the Consumer
            Credit Act 1974. By providing your electronic acceptance below, you
            will be legally bound by its terms
          </p>
          <p
            className={clsx(classes.subText, {
              bsm: brand === "BSM",
            })}
          >
            Once you have submitted your application and provided us with all
            required Documentation (as set out in your Franchise Agreement), we
            will be in touch to confirm next steps. The required Documentation
            will need to be submitted a minimum of 10 business days prior to
            your preferred start date (provided that all applications remain
            subject to our approval and satisfaction of any Conditions
            Precedent).
          </p>
          <FormControlLabel
            control={
              <Checkbox
                disabled={!isDocumentsRead}
                checked={isTermsConditionschecked}
                onChange={() =>
                  setsTermsConditionschecked(!isTermsConditionschecked)
                }
                name="checkedB"
                color="primary"
              />
            }
            className={clsx(classes.formControlLabel, {
              bsm: brand === "BSM",
            })}
            label={
              brand === "BSM"
                ? "I accept and agree to the terms of my agreement with BSM Developments Limited set out in the above documents"
                : "I accept and agree to the terms of my agreement with Automobile Association Developments Limited set out in the above documents"
            }
          />
          <p
            className={clsx(classes.privacyText, {
              bsm: brand === "BSM",
            })}
          >
            Our{" "}
            <a
              className={clsx(classes.boldLink, {
                bsm: brand === "BSM",
              })}
              href={urlPrivacyPolicy}
            >
              privacy notice
            </a>{" "}
            explains how and why we use your personal information, including
            what details we hold, who we might share it with, and your choices
            and rights. It also includes information about any checks and
            decisions we may make.
          </p>
        </div>
        <AcceptanceButton
          disabled={!isTermsConditionschecked || isSigningDocuments}
          loading={isSigningDocuments}
          onClick={e => {
            e.preventDefault();
            signDocuments({
              variables: {
                agreementKey,
              },
            });
          }}
          brand={brand}
        >
          Accept franchise
        </AcceptanceButton>
        <Box marginTop="48px">
          <Typography
            className={clsx(classes.smallText, {
              bsm: brand === "BSM",
            })}
          >
            If you wish to cancel this application, you can close this window
            or&nbsp;
            <a
              className={clsx(classes.boldLink, {
                bsm: brand === "BSM",
              })}
              href={
                brand === "BSM"
                  ? "https://www.bsm.co.uk/"
                  : "https://www.theaa.com/driving-school"
              }
            >
              exit to the driving school hompage
            </a>
          </Typography>
        </Box>
        <AcceptanceFooter brand={brand} />
      </div>
    </>
  );
};

export default FranAcceptanceDocumentsPage;
